<template>
  <v-container fluid class="create-article-container">
    <v-card class="mx-auto quill-card" max-width="1080">
      <v-card-title>Edit Handbook Article</v-card-title>
      <v-card-text>
        <v-form @submit.prevent="updateArticle">
          <v-alert
            v-if="formError"
            type="error"
            variant="tonal"
            class="mb-4"
          >
            {{ formError }}
          </v-alert>

          <!-- Category -->
          <v-select
            v-model="form.category"
            :items="categories"
            item-title="name"
            item-value="id"
            return-object
            label="Category"
            required
          />

          <!-- Title -->
          <v-text-field
            v-model="form.title"
            label="Title"
            required
            :error-messages="errors.title"
            :disabled="saving"
          />

          <!-- Description -->
          <v-textarea
            v-model="form.description"
            label="Short description (shown on dashboard)"
            counter="255"
            auto-grow
            class="mb-4"
          />

          <!-- Tiptap Editor -->
          <EditorView :editor="editor" />

          <!-- Flags -->
          <div class="checkbox-group">
            <v-checkbox
              v-model="form.visible_to_onboarding"
              label="Visible to onboarding"
            />
          </div>
        </v-form>
      </v-card-text>
      <v-card-actions class="button-group">
        <v-btn color="primary" :loading="saving" :disabled="saving" @click="updateArticle">Save Changes</v-btn>
        <v-btn text @click="$router.push('/handbook')">Cancel</v-btn>
      </v-card-actions>
    </v-card>
  </v-container>
</template>

<script>
import api from '@/plugins/axios'
import { createEditor } from '@/tiptap/components/EditorCore.js'
import EditorView from '@/tiptap/components/EditorView.vue'
import { normalizeForSave } from '@/tiptap/diff/normalizeForSave'

export default {
  name: 'HandbookArticleEdit',
  props: ['id'],
  components: { EditorView },
  data() {
    return {
      form: {
        category: null,
        title: '',
        description: '',
        visible_to_onboarding: false,
        content_json: null,
      },
      categories: [],
      articles: [],
      errors: { title: [] },
      formError: '',
      saving: false,
      editor: null,
      initialLoaded: false,
    }
  },
  created() {
    // Create editor with same feature set as Create page
    this.editor = createEditor({
      content: '<p></p>',
      features: {
        tables: true,
        images: true,
        video: true,
        links: true,
        codeBlock: true,
        quote: true,
        highlight: true,
        color: true,
        strike: true,
        bulletList: true,
        orderedList: true,
        taskList: true,
        indent: true,
        horizontalRule: true,
        mentions: true,
        userMentions: false,
        heading: true,
        fontFamily: true,
        fontSize: true,
        textAlign: true,
        clear: true,
        subSuper: true,
      },
    })
  },
  beforeUnmount() {
    if (this.editor?.destroy) this.editor.destroy()
  },
  methods: {
    async fetchCategories() {
      try {
        const res = await api.get('/api/handbook-categories/', { withCredentials: true })
        this.categories = res.data
        // Map numeric category id to object after categories load
        if (this.form.category != null && typeof this.form.category === 'number') {
          const found = this.categories.find(c => c.id === this.form.category)
          if (found) this.form.category = found
        }
      } catch (e) {
        console.error('Error fetching categories:', e)
      }
    },
    async fetchArticles() {
      try {
        const res = await api.get('/api/handbook-sections/suggestions/', { withCredentials: true })
        this.articles = res.data
      } catch (e) {
        console.error('Error fetching articles:', e)
      }
    },
    async fetchArticle() {
      try {
        const res = await api.get(`/api/handbook-sections/${this.id}/`, { withCredentials: true })
        const a = res.data || {}
        // Fill basic fields
        this.form.title = a.title || ''
        this.form.description = a.description || ''
        this.form.visible_to_onboarding = !!a.visible_to_onboarding
        // category may be id or object; store id for now (will map to object after categories)
        this.form.category = typeof a.category === 'object' ? a.category : a.category ?? null

        // Prefer TipTap JSON if present, else fall back to HTML
        const json = a.content_json || null
        const html = a.content_html || a.content || '<p></p>'

        if (json) {
          // Load as-is; existing ids must be preserved while editing
          this.editor?.commands?.setContent(json, true)
        } else {
          this.editor?.commands?.setContent(html, true)
        }

        this.initialLoaded = true
      } catch (e) {
        console.error('Error fetching article:', e)
        this.formError = 'Failed to load the article.'
      }
    },
    async updateArticle() {
      this.errors = { title: [] }
      this.formError = ''
      this.saving = true
      try {
        const tiptap = this.editor
        if (!tiptap) {
          this.formError = 'Editor not ready.'
          this.saving = false
          return
        }

        // Extract TipTap payloads (and ensure live PM doc carries the same ids)
        const jsonRaw = tiptap.getJSON()
        const json = normalizeForSave(jsonRaw)
        // keep the editor state in sync with the normalized JSON; no history step
        tiptap.commands.setContent(json, false)
        const html = tiptap.getHTML()
        const text = tiptap.getText()

        // Normalize category to id
        const categoryId =
          this.form.category && typeof this.form.category === 'object'
            ? this.form.category.id
            : this.form.category

        const payload = {
          title: this.form.title,
          description: this.form.description,
          visible_to_onboarding: this.form.visible_to_onboarding,
          category: categoryId,
          content_json: json,
          content_html: html,
          content_text: text,
          content: html,
        }

        await api.put(`/api/handbook-sections/${this.id}/`, payload, { withCredentials: true })
        this.$router.push('/handbook')
      } catch (err) {
        const status = err?.response?.status
        const data = err?.response?.data || {}
        if (data.title) this.errors.title = Array.isArray(data.title) ? data.title : [String(data.title)]
        else if (data.detail) this.formError = String(data.detail)
        else if (data.non_field_errors) {
          this.formError = (Array.isArray(data.non_field_errors) ? data.non_field_errors : [String(data.non_field_errors)]).join(' ')
        } else if (status === 400) {
          this.formError = 'Bad request: ' + JSON.stringify(data)
        } else {
          this.formError = 'Failed to save the article. Please try again.'
        }
        console.error('Update failed', err)
      } finally {
        this.saving = false
      }
    },
  },
  async mounted() {
    await this.fetchArticle()
    await this.fetchCategories()
    await this.fetchArticles()
  },
}
</script>

<style scoped>
.create-article-container {
  background: linear-gradient(to bottom, #fafafa, #e7effa, #d2e3f5);
  min-height: 100vh;
  padding: 20px;
}
/* Allow popovers/menus to escape the card */
.quill-card {
  overflow: visible !important;
}
.checkbox-group {
  display: flex;
  gap: 16px;
  align-items: center;
  margin-top: 10px;
}
.button-group {
  display: flex;
  justify-content: flex-start;
  margin-top: -10px;
  padding-bottom: 10px;
}
</style>
